import classNames from 'classnames'
import * as React from 'react'
import {useRef, useState} from 'react'
import type {Alignment, Index} from 'react-virtualized'
import type {Size} from 'react-virtualized/dist/es/AutoSizer'
import AutoSizer from 'react-virtualized/dist/es/AutoSizer'
import type {MeasuredCellParent} from 'react-virtualized/dist/es/CellMeasurer'
import {CellMeasurerCache} from 'react-virtualized/dist/es/CellMeasurer'
import type {GridCoreProps, ScrollParams} from 'react-virtualized/dist/es/Grid'
import type {ListRowProps, RenderedRows} from 'react-virtualized/dist/es/List'
import List from 'react-virtualized/dist/es/List'

import {ShowPopupProvider} from './VirtualizedTree.contexts'

import styles from './VirtualizedTree.css'

type Item = {
  level: number
}
export type VirtualizedTreeNodeProps = {
  index: number
  style: React.CSSProperties
  height: number
  width: number
  indent: number
  parent: React.Component<GridCoreProps> & MeasuredCellParent
  cache: CellMeasurerCache
}
export type VirtualizedTreeChildProps<I extends Item> = VirtualizedTreeNodeProps & {
  item: I
}
type Props<I extends Item> = {
  ariaLabel: string
  flatItems: readonly I[]
  itemHeight: number | ((item: I, index: number) => number)
  className?: string
  tabIndex?: number | null
  firstLevelIndent?: number
  levelIndent?: number
  scrollToIndex?: number
  scrollToAlignment?: Alignment
  shouldScrollToIndex?: boolean
  children: (props: VirtualizedTreeChildProps<I>) => React.ReactNode
  onKeyDown?: (event: React.KeyboardEvent) => void
  onRowsRendered?: (info: RenderedRows) => void
  onScroll?: (params: ScrollParams) => void
  listClassName?: string
}
const DEFAULT_LEVEL_INDENT = 24

function VirtualizedTree<I extends Item>(
  {
    ariaLabel,
    flatItems,
    itemHeight,
    className,
    tabIndex,
    levelIndent = DEFAULT_LEVEL_INDENT,
    firstLevelIndent = levelIndent,
    scrollToIndex = -1,
    scrollToAlignment = 'auto',
    shouldScrollToIndex = true,
    children,
    onKeyDown,
    onRowsRendered,
    onScroll,
    listClassName,
  }: Props<I>,
  ref: React.Ref<List>,
) {
  const [scrollBarWidth, setScrollBarWidth] = React.useState(0)
  const onScrollbarPresenceChange = React.useCallback(
    ({size, vertical}: {size: number; vertical: boolean}) =>
      vertical ? setScrollBarWidth(size) : setScrollBarWidth(0),
    [],
  )
  const itemsCount = flatItems.length
  const rowHeight = React.useMemo(
    () =>
      typeof itemHeight === 'function'
        ? ({index}: Index) => itemHeight(flatItems[index], index)
        : itemHeight,
    [flatItems, itemHeight],
  )

  const [cache] = useState(
    new CellMeasurerCache({
      fixedHeight: true,
    }),
  )

  const previousItems = useRef(flatItems)

  if (previousItems.current !== flatItems) {
    cache.clearAll?.()
    previousItems.current = flatItems
  }

  const rowRenderer = React.useCallback(
    ({index, style, parent}: ListRowProps) => {
      const item = flatItems[index]
      const height = typeof rowHeight === 'function' ? rowHeight({index}) : rowHeight
      const width = parent.props.width - scrollBarWidth
      const indent = firstLevelIndent + item.level * levelIndent
      return children({
        item,
        index,
        style,
        width,
        height,
        indent,
        parent,
        cache,
      })
    },
    [flatItems, rowHeight, scrollBarWidth, firstLevelIndent, levelIndent, children, cache],
  )
  const scrollProps = React.useMemo(
    () =>
      shouldScrollToIndex && scrollToIndex !== -1
        ? {
            scrollToIndex,
            scrollToAlignment,
          }
        : null,
    [shouldScrollToIndex, scrollToIndex, scrollToAlignment],
  )
  const renderList = React.useCallback(
    ({width, height}: Size) => (
      <List
        ariaLabel={ariaLabel}
        ref={ref}
        className={classNames(styles.list, listClassName)}
        width={width}
        height={height}
        rowRenderer={rowRenderer}
        rowCount={itemsCount}
        rowHeight={rowHeight}
        onScrollbarPresenceChange={onScrollbarPresenceChange}
        onRowsRendered={onRowsRendered}
        onScroll={onScroll}
        tabIndex={tabIndex}
        {...scrollProps}
      />
    ),
    [
      ariaLabel,
      ref,
      listClassName,
      rowRenderer,
      itemsCount,
      rowHeight,
      onScrollbarPresenceChange,
      onRowsRendered,
      onScroll,
      tabIndex,
      scrollProps,
    ],
  )
  return (
    <ShowPopupProvider>
      {React.useMemo(
        () => (
          <div
            role="presentation"
            className={classNames(styles.wrapper, className)}
            onKeyDown={onKeyDown}
          >
            <AutoSizer>{renderList}</AutoSizer>
          </div>
        ),
        [className, renderList, onKeyDown],
      )}
    </ShowPopupProvider>
  )
}

export default React.forwardRef(VirtualizedTree) as <I extends Item>(
  props: Props<I> & {
    ref?: React.Ref<List>
  },
) => React.ReactElement | null
